import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// Services & API
import CashierService from "../../api/cashier";

// Redux
import { setCashier } from "../../store/actions/cashierSlice";

// APP COMPONENTS
import CashierRow from "./table/cashierRow";
import SearchFilter from "../search/searchFilter";
import CashierInfoModal from "./cashierInfoModal";
import DeliveryOperatorFilter from "../search/deliveryOperatorFilter";

// Material UI
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

// Icons
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import PageHeaderLight from "../ui/PageHeaderLight";
import FilterListIcon from "@mui/icons-material/FilterList";

export default function Cashiers() {
  const theme = useTheme();
  const dispatch = useDispatch();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [cashiers, setCashiers] = useState([]);
  const [showButtonMore, setShowButtonMore] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(true);

  // Modal state
  const [showCashierInfoModal, setShowCashierInfoModal] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    newSearch: false,
    operator: {
      label: "Todos",
      value: "ALL",
    },
  });

  // Initial load
  useEffect(() => {
    loadCashiers(true);
  }, []);

  // Search with filters
  const search = async (filtersData) => {
    setPage(1);
    setLoading(true);
    setError(null);

    try {
      const filtersObj = {
        operator: filtersData[DeliveryOperatorFilter.name] ?? filters.operator,
      };
      setFilters(filtersObj);

      await loadCashiers(true, filtersObj);
      setSubmitted(true);
    } catch (e) {
      setError("Erro ao buscar registros de caixa. Tente novamente.");
      console.error("Search error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Load cashier data
  const loadCashiers = async (newSearch, filtersData = null) => {
    try {
      if (newSearch) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      let pageQuery = page;
      if (newSearch) {
        pageQuery = 1;
        setPage(1);
      }

      const params = {
        page: pageQuery,
        operator: filtersData
          ? filtersData.operator.value
          : filters.operator.value,
      };

      const { data } = await CashierService.getCashierHistory(params);

      // Transform data to ensure it has the expected properties for CashierRow
      const transformedData = data.map((cashier) => ({
        ...cashier,
        first_name: cashier.username || cashier.first_name || "N/A",
        last_name: cashier.last_name || "",
        date_time_begin:
          cashier.created_at ||
          cashier.date_time_begin ||
          new Date().toISOString(),
      }));

      let result;
      if (newSearch) {
        result = [...transformedData];
        switchShowButtonMore(transformedData.length);
      } else {
        const new_cashiers = transformedData.filter(
          ({ id: id1 }) => !cashiers.some(({ id: id2 }) => id2 === id1)
        );
        result = [...cashiers, ...new_cashiers];
        switchShowButtonMore(new_cashiers.length);
      }

      setPage(pageQuery + 1);
      setCashiers(result);
      setSubmitted(true);
    } catch (e) {
      setError("Erro ao carregar registros de caixa. Tente novamente.");
      console.error("Error loading cashiers:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Update show more button state
  const switchShowButtonMore = (length) => {
    setShowButtonMore(length > 0);
  };

  // Handle modal
  const handleShowCashierInfoModal = (cashier = null) => {
    dispatch(setCashier(cashier));
    setShowCashierInfoModal(!showCashierInfoModal);
  };

  // Toggle filters visibility
  const toggleFilters = () => {
    setFiltersVisible(!filtersVisible);
  };

  const pageHeaderActions = [
    {
      key: "filtros",
      variant: "outlined",
      startIcon: <FilterListIcon />,
      onClick: toggleFilters,
      children: "Filtros",
    },
  ];

  // Render empty state
  const renderEmptyState = () => (
    <Box sx={{ py: 8, textAlign: "center" }}>
      <Box sx={{ animation: "float 2s ease-in-out infinite alternate" }}>
        <AccountBalanceWalletIcon
          sx={{
            fontSize: 64,
            color: alpha(theme.palette.text.secondary, 0.3),
            mb: 2,
          }}
        />
      </Box>

      <Typography variant="h6" color="textSecondary" gutterBottom>
        {submitted
          ? "Nenhum registro de caixa encontrado"
          : "Aplique filtros para visualizar os registros"}
      </Typography>

      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ maxWidth: 500, mx: "auto", mb: 4 }}
      >
        {submitted
          ? "Não foram encontrados registros de caixa com os filtros aplicados. Tente ajustar os critérios de busca."
          : "Selecione um operador para visualizar seus registros de caixa."}
      </Typography>

      <Button
        variant={submitted ? "outlined" : "contained"}
        startIcon={submitted ? <TuneIcon /> : <SearchIcon />}
        onClick={toggleFilters}
        sx={{
          borderRadius: 2,
          px: 3,
          py: 1,
          "&:hover": {
            transform: "scale(1.05)",
            boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
          },
          "&:active": {
            transform: "scale(0.95)",
          },
        }}
      >
        {submitted ? "Ajustar Filtros" : "Aplicar Filtros"}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 4, md: 6 }, maxWidth: "1800px", mx: "auto" }}>
      <PageHeaderLight
        title="Registro de Caixas"
        subtitle="Histórico de Registro de Caixas"
        actions={pageHeaderActions}
        filters={
          <SearchFilter
            title=""
            filters={[DeliveryOperatorFilter]}
            filterDate
            submit={search}
            initialExpanded
            showHeader={false}
          />
        }
        expandedFilters={filtersVisible}
        toggleFilters={toggleFilters}
      />

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="body1" color="textSecondary">
            Carregando registros de caixa...
          </Typography>
        </Box>
      )}

      {/* Content Section */}
      {!loading && (
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(10px)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              {cashiers.length > 0 ? (
                <Box sx={{ display: "block", width: "100%" }}>
                  {/* Directly render each cashier row without AnimatePresence */}
                  {cashiers.map((cashier, index) => (
                    <Box
                      key={cashier.id || index}
                      sx={{ display: "block", width: "100%" }}
                    >
                      <CashierRow
                        index={index + 1}
                        cashier={cashier}
                        openAction={() => handleShowCashierInfoModal(cashier)}
                      />
                    </Box>
                  ))}

                  {/* Load more button */}
                  <Button
                    title="Carregar mais"
                    size="large"
                    fullWidth
                    disabled={!showButtonMore || refreshing}
                    startIcon={
                      refreshing ? (
                        <CircularProgress size={20} />
                      ) : (
                        <ExpandCircleDownIcon />
                      )
                    }
                    onClick={() => loadCashiers(false)}
                    sx={{
                      mt: 2,
                      py: 1.2,
                      borderRadius: 2,
                      color: theme.palette.text.secondary,
                      bgcolor: theme.palette.grey[50],
                      border: `1px solid ${theme.palette.grey[200]}`,
                      "&:hover": {
                        bgcolor: theme.palette.grey[100],
                        borderColor: theme.palette.grey[300],
                        transform: "scale(1.01)",
                      },
                      "&:active": {
                        transform: "scale(0.99)",
                      },
                      "&.Mui-disabled": {
                        color: alpha(theme.palette.text.secondary, 0.4),
                        bgcolor: alpha(theme.palette.grey[100], 0.5),
                      },
                    }}
                  >
                    {refreshing
                      ? "Carregando..."
                      : showButtonMore
                      ? "Mostrar Mais Registros"
                      : "Não há mais registros para exibir"}
                  </Button>
                </Box>
              ) : (
                renderEmptyState()
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Modal */}
      {showCashierInfoModal && (
        <CashierInfoModal
          show={showCashierInfoModal}
          closeModal={handleShowCashierInfoModal}
        />
      )}

      {/* Add float animation keyframes */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          100% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </Box>
  );
}
